/** SUNTRONIX ONE — Administration → ONE Connect → SMS (Exotel adapter). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageSquareText, Plug, Send } from "lucide-react";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

export default function OneSms() {
  const [st, setSt] = useState(null);
  const [cfg, setCfg] = useState({ provider: "EXOTEL", api_key: "", api_token: "",
    account_sid: "", subdomain: "api.in.exotel.com", sender_id: "", dlt_entity_id: "" });
  const [templates, setTemplates] = useState([]);
  const [msgs, setMsgs] = useState({ items: [], summary: {} });
  const [tpl, setTpl] = useState({ name: "", dlt_template_id: "", content: "" });
  const [test, setTest] = useState({ to: "", template_id: "" });

  const load = useCallback(() => {
    api.get("/sms/status").then(({ data }) => setSt(data));
    api.get("/sms/templates").then(({ data }) => setTemplates(data.items || []));
    api.get("/sms/messages").then(({ data }) => setMsgs(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    try { await api.put("/sms/config", cfg);
      toast.success("Credentials saved (encrypted at rest)"); load();
    } catch (e) { toast.error(JSON.stringify(e.response?.data?.detail) || "Save failed"); }
  };
  const testConn = async () => {
    try { const { data } = await api.post("/sms/test-connection");
      (data.ok ? toast.success : toast.error)(data.verdict); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const addTpl = async () => {
    try { await api.post("/sms/templates", tpl);
      toast.success("Template saved"); setTpl({ name: "", dlt_template_id: "", content: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendTest = async () => {
    try { const { data } = await api.post("/sms/send", { to: test.to, template_id: test.template_id });
      toast.success(`SMS ${data.message.status} — ${data.message.provider_sid || ""}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="max-w-[1100px] mx-auto space-y-4" data-testid="one-sms-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-teal-900 text-white grid place-items-center">
          <MessageSquareText className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="text-xl font-heading font-semibold">ONE SMS</div>
          <div className="text-[11px] text-slate-500">Provider adapter (Exotel · India DLT) — template-based operational SMS</div>
        </div>
        {st && (
          <span data-testid="sms-status-badge"
              className={"text-[10px] font-semibold px-2 py-1 rounded " +
                (st.status === "READY" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
            {st.status.replace(/_/g, " ")}
          </span>)}
      </header>

      {st?.can_configure && (
        <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="sms-config-card">
          <div className="text-[12px] font-semibold mb-2 flex items-center gap-2">
            <Plug className="h-3.5 w-3.5" /> Configure Exotel
            {st.configured && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">CONFIGURED — re-enter to rotate</span>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["api_key", "API Key *", "text"], ["api_token", "API Token *", "password"],
              ["account_sid", "Account SID *", "text"], ["sender_id", "DLT Sender ID / Header *", "text"],
              ["dlt_entity_id", "DLT Principal Entity ID *", "text"]].map(([k, l, t]) => (
              <label key={k} className="text-[10px] text-slate-500">{l}
                <input type={t} value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                    data-testid={`sms-cfg-${k}`} className={inputCls} autoComplete="off" />
              </label>))}
            <label className="text-[10px] text-slate-500">Region
              <select value={cfg.subdomain} onChange={(e) => setCfg({ ...cfg, subdomain: e.target.value })}
                  data-testid="sms-cfg-subdomain" className={inputCls}>
                <option value="api.in.exotel.com">India — api.in.exotel.com</option>
                <option value="api.exotel.com">Singapore — api.exotel.com</option>
              </select></label>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={saveCfg} data-testid="sms-cfg-save"
                className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white">Save credentials</button>
            <button onClick={testConn} data-testid="sms-test-connection"
                className="text-[11px] px-3 py-1.5 rounded-md border border-teal-700 text-teal-800">Test connection</button>
            {st?.last_test && (
              <span className={"text-[10px] self-center " + (st.last_test.ok ? "text-emerald-700" : "text-rose-700")}>
                Last test: {st.last_test.verdict}</span>)}
          </div>
          <div className="text-[9.5px] text-slate-400 mt-2">
            Credentials are encrypted at rest and never returned to the browser.
          </div>
        </div>)}

      <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="sms-templates-card">
        <div className="text-[12px] font-semibold mb-2">Approved DLT templates ({templates.length})</div>
        {templates.map((t) => (
          <div key={t.id} className="flex items-center gap-2 text-[11px] border-t border-slate-100 py-1.5"
              data-testid={`sms-tpl-${t.id}`}>
            <b className="w-48">{t.name}</b>
            <span className="font-mono text-[9.5px] text-slate-500">{t.dlt_template_id}</span>
            <span className="flex-1 text-slate-600 truncate">{t.content}</span>
            {st?.can_configure && (
              <button onClick={async () => { await api.delete(`/sms/templates/${t.id}`); load(); }}
                  className="text-[10px] text-rose-600">✕</button>)}
          </div>))}
        {st?.can_configure && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            <input placeholder="Name (e.g. DISPATCH CONFIRMATION)" value={tpl.name}
                onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
                data-testid="sms-tpl-name" className={inputCls} />
            <input placeholder="DLT Template ID" value={tpl.dlt_template_id}
                onChange={(e) => setTpl({ ...tpl, dlt_template_id: e.target.value })}
                data-testid="sms-tpl-dltid" className={inputCls + " font-mono"} />
            <input placeholder="Approved content — use {var} placeholders" value={tpl.content}
                onChange={(e) => setTpl({ ...tpl, content: e.target.value })}
                data-testid="sms-tpl-content" className={inputCls} />
            <button onClick={addTpl} data-testid="sms-tpl-add"
                className="text-[11px] px-2 py-1.5 rounded-md bg-slate-900 text-white mt-1">+ Add template</button>
          </div>)}
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="sms-send-card">
        <div className="text-[12px] font-semibold mb-2 flex items-center gap-2">
          <Send className="h-3.5 w-3.5" /> Send test SMS</div>
        <div className="flex gap-2 items-end">
          <label className="text-[10px] text-slate-500">Mobile (+91…)
            <input value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })}
                data-testid="sms-send-to" className={inputCls + " w-44 font-mono"} /></label>
          <label className="text-[10px] text-slate-500 flex-1">Approved template
            <select value={test.template_id} onChange={(e) => setTest({ ...test, template_id: e.target.value })}
                data-testid="sms-send-template" className={inputCls}>
              <option value="">— pick —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>
          <button onClick={sendTest} data-testid="sms-send-btn"
              className="text-[11px] px-3 py-1.5 rounded-md bg-teal-800 text-white">Send</button>
        </div>
        {test.template_id && (
          <div className="text-[10px] text-slate-500 mt-2" data-testid="sms-preview">
            Preview: {(templates.find((t) => t.id === test.template_id) || {}).content}
          </div>)}
      </div>

      <div className="border border-slate-200 rounded-lg bg-white" data-testid="sms-log-card">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold flex gap-3">
          Message log
          <span className="text-slate-500 font-normal">
            Sent {msgs.summary?.sent ?? 0} · Delivered {msgs.summary?.delivered ?? 0} ·
            Failed {msgs.summary?.failed ?? 0} · Rate {msgs.summary?.delivery_rate ?? 0}%</span>
        </div>
        <table className="w-full text-[11px]"><tbody>
          {(msgs.items || []).slice(0, 25).map((m) => (
            <tr key={m.id} className="border-t border-slate-100" data-testid={`sms-msg-${m.id}`}>
              <td className="px-3 py-1.5 font-mono text-[10px]">{m.to}</td>
              <td className="px-3 py-1.5">{m.template_name}</td>
              <td className="px-3 py-1.5">
                <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                    (m.status === "DELIVERED" ? "bg-emerald-100 text-emerald-800"
                      : m.status === "FAILED" ? "bg-rose-100 text-rose-800" : "bg-blue-100 text-blue-800")}>
                  {m.status}</span>
                {m.failure_reason && <span className="ml-1 text-[9px] text-rose-600">{m.failure_reason}</span>}
              </td>
              <td className="px-3 py-1.5 text-[10px] text-slate-500">{m.sent_by}<br />{m.sent_at?.slice(0, 16)}</td>
            </tr>))}
          {!(msgs.items || []).length && (
            <tr><td className="px-3 py-6 text-center text-slate-400 text-[11px]">No messages sent yet.</td></tr>)}
        </tbody></table>
      </div>
    </div>
  );
}
